"use client"

import { datadogLogs } from "@datadog/browser-logs"

import { env } from "@/lib/runtime/env"
import { logsBeforeSend } from "@/platform/observability/datadog/logs-before-send"
import { recordTelemetry } from "@/platform/observability/telemetry"

let initialized = false

export const startBrowserLogs = (): void => {
  const clientToken = env.datadogClientToken
  if (initialized || typeof window === "undefined" || !clientToken) return
  recordTelemetry(() => {
    datadogLogs.init({
      clientToken,
      site: "datadoghq.com",
      ...(process.env.NEXT_PUBLIC_E2E === "true"
        ? { proxy: ({ path, parameters }: { path: string; parameters: string }) => `${env.apiBaseUrl}/__observability__${path}?${parameters}` }
        : {}),
      service: "marketplace-storefront",
      env: env.publicVercelEnv ?? "development",
      version: env.appVersion,
      sessionSampleRate: 100,
      forwardErrorsToLogs: true,
      forwardReports: [],
      sessionPersistence: "cookie",
      trackSessionAcrossSubdomains: false,
      useSecureSessionCookie: process.env.NEXT_PUBLIC_E2E !== "true",
      beforeSend: logsBeforeSend,
    })
    if (env.nodeEnv === "development" || env.publicVercelEnv === "preview") datadogLogs.logger.setHandler("console")
    initialized = true
  })
}

export const browserLogs = {
  info: (message: string, context?: Record<string, unknown>) =>
    recordTelemetry(() => {
      startBrowserLogs()
      if (initialized) datadogLogs.logger.info(message, context)
    }),
  warn: (message: string, context?: Record<string, unknown>, error?: Error) =>
    recordTelemetry(() => {
      startBrowserLogs()
      if (initialized) datadogLogs.logger.warn(message, context, error)
    }),
  error: (message: string, context?: Record<string, unknown>, error?: Error) =>
    recordTelemetry(() => {
      startBrowserLogs()
      if (initialized) datadogLogs.logger.error(message, context, error)
    }),
  setUser: (user: { id: string } & Record<string, unknown>) => recordTelemetry(() => datadogLogs.setUser(user)),
  setUserProperty: (key: string, value: unknown) => recordTelemetry(() => datadogLogs.setUserProperty(key, value)),
  clearUser: () => recordTelemetry(() => datadogLogs.clearUser()),
  setGlobalContextProperty: (key: string, value: unknown) => recordTelemetry(() => datadogLogs.setGlobalContextProperty(key, value)),
  removeGlobalContextProperty: (key: string) => recordTelemetry(() => datadogLogs.removeGlobalContextProperty(key)),
}
